import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import classNames from "classnames";
import styles from "./Field.module.scss";
import Icon from "../images/Icon";

export interface FieldFileProps {
  value?: string | number;
  readOnly?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string | File ) => void;
  width?: string;
  height?: string;
  glowColor?: string;
  baseURL?: string;
  extension?: string;
}

export const FieldFile = ({
  value = "",
  readOnly,
  onChange,
  width = "100%",
  height = "fit-content",
  glowColor = "var(--color-Field-Focus-normal)"     ,
  baseURL = "",
  extension = "",
}: FieldFileProps) => {
  const fieldStyle = { "--glow-color": glowColor } as React.CSSProperties;

  const [, setSrc] = useState("");
  const [inputValue, setInputValue] = useState(value);
  const [isBlob, setIsBlob] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);


  useEffect(() => {
    if (!hasFocus) {
      setInputValue(value);

      if (value && typeof value === "string" && !isBlob) {
        const fullPath = constructFullPath(value, baseURL, extension);

        setSrc(fullPath);
      }

    }
  }, [value, baseURL, extension, hasFocus]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>, isFile: boolean = false) => {
    const input = event.target.value;

    if (isFile && event.target.files) {
      const file = event.target.files[0];

      if (file) {
        if (file.size > 0) {
          setInputValue(file.name);
          const objectURL = URL.createObjectURL(file);
          setSrc(objectURL);
          setIsBlob(true);


            if (onChange) onChange(event, file);

        } else {
          console.error("File is empty");
        }
      }
    } else {
      if (!isBlob) {
        const fullPath = constructFullPath(input, baseURL, extension);
        setSrc(fullPath);
      }

      setInputValue(input);
      if (onChange) onChange(event, input);
    }
  };

  const fileInputId = useRef(`file-input-hidden-${Math.random().toString(36).substr(2, 9)}`);

  const renderInput = (): JSX.Element => {
    const fileInputProps = {
      className: classNames(styles.file_input_hidden),
      onChange: (e: ChangeEvent<HTMLInputElement>) => handleChange(e, true),
      id: fileInputId.current,
    };


        return (
          <div className={classNames(styles.file_input_wrapper)}>
            <div className={classNames(styles.field_with_icon)}>
              <input {...fileInputProps} type="file" />
              <input
                type="text"
                className={classNames(styles.field_input)}
                value={typeof inputValue === "string" ? inputValue : ""}
                onFocus={() => setHasFocus(true)}
                onBlur={() => setHasFocus(false)}
              />
              <Icon
                onClick={() => {
                  const fileInput = document.getElementById(fileInputId.current);
                  if (fileInput) {
                    fileInput.click();
                  }
                }}
                name="upload"
                color="var(--color-Field-Icon-normal)"
                hoverColor="var(--color-Field-Icon-hover-normal)"
              />
            </div>
          </div>
        );
  };

  const constructFullPath = (value: string, baseURL: string, extension: string) => {
    if (value.startsWith("http://")) {
      return value;
    }

    let fullPath = value;
    if (!value.startsWith("http") && !value.startsWith("/")) {
      fullPath = `${baseURL}${value}`;
    }
    if (!value.includes(".")) {
      fullPath += `${extension}`;
    }

    return fullPath;
  };


  return (
    <div className={classNames(styles.field)} style={{ width, height, ...fieldStyle }}>
      {renderInput()}
    </div>
  );
};

export default FieldFile;
